import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import api from '../utils/axios';
import { toast } from 'sonner';
import {
  Coins, Download, Loader2, RefreshCw, ChevronDown, ChevronRight,
  Building2, TrendingUp, FileSpreadsheet, CalendarClock, Ban, Search,
} from 'lucide-react';

const fmtMoney = (n) =>
  '$ ' + new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtInt = (n) => new Intl.NumberFormat('es-AR').format(Math.round(Number(n) || 0));
const periodoLabel = (p) => {
  if (!p || p.length !== 7) return p;
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${meses[parseInt(p.slice(5, 7), 10) - 1]} ${p.slice(0, 4)}`;
};

const defaultMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1); // mes anterior (a mes vencido)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function ComisionesWaya() {
  const [meses, setMeses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState({}); // periodo -> full doc | 'loading'
  const [scrapeMonth, setScrapeMonth] = useState(defaultMonth());
  const [backfillN, setBackfillN] = useState(6);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExcl, setShowExcl] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [catalogoInfo, setCatalogoInfo] = useState({ actualizado: null });
  const [exclSet, setExclSet] = useState(new Set());
  const [exclSearch, setExclSearch] = useState('');
  const [savingExcl, setSavingExcl] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    loadMeses();
    checkActivo();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const loadMeses = async () => {
    try {
      const r = await api.get('/comisiones-waya/meses');
      const list = r.data?.meses || [];
      setMeses(list);
      setSelected((prev) => (prev.size ? prev : new Set(list.slice(0, 3).map((m) => m.periodo))));
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  const checkActivo = async () => {
    try {
      const r = await api.get('/comisiones-waya/activo');
      if (r.data?.job) startPolling(r.data.job.id, r.data.job);
    } catch (e) { /* noop */ }
  };

  const startPolling = (jobId, initial, onDone) => {
    if (initial) setJob(initial);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/comisiones-waya/job/${jobId}`);
        const j = r.data;
        setJob(j);
        if (j.estado === 'completado' || j.estado === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (j.estado === 'completado') {
            toast.success(j.tipo === 'catalogo' ? 'Lista de empresas actualizada' : 'Comisiones actualizadas');
            onDone ? onDone() : loadMeses();
          } else {
            toast.error('Error: ' + (j.error || 'desconocido'));
          }
        }
      } catch (e) { /* noop */ }
    }, 3000);
  };

  const actualizarMes = async () => {
    if (!scrapeMonth) return;
    try {
      const r = await api.post('/comisiones-waya/actualizar', { periodo: scrapeMonth });
      toast.info(`Trayendo comisiones de ${periodoLabel(scrapeMonth)}… (puede tardar unos minutos)`);
      startPolling(r.data.job_id, { estado: 'procesando', periodos: r.data.periodos, mes_total: 1 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar');
    }
  };

  const lanzarBackfill = async () => {
    try {
      const r = await api.post('/comisiones-waya/backfill', { meses: Number(backfillN) || 6 });
      toast.info(`Trayendo últimos ${r.data.periodos.length} meses…`);
      startPolling(r.data.job_id, { estado: 'procesando', periodos: r.data.periodos, mes_total: r.data.periodos.length });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar');
    }
  };

  const toggleSelect = (periodo) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(periodo) ? n.delete(periodo) : n.add(periodo);
      return n;
    });
  };

  const toggleExpand = async (periodo) => {
    if (expanded[periodo]) {
      setExpanded((p) => { const n = { ...p }; delete n[periodo]; return n; });
      return;
    }
    setExpanded((p) => ({ ...p, [periodo]: 'loading' }));
    try {
      const r = await api.get(`/comisiones-waya/mes/${periodo}`);
      setExpanded((p) => ({ ...p, [periodo]: r.data }));
    } catch (e) {
      setExpanded((p) => { const n = { ...p }; delete n[periodo]; return n; });
      toast.error('No se pudo cargar el detalle');
    }
  };

  const exportExcel = async () => {
    const periodos = Array.from(selected);
    try {
      const r = await api.get('/comisiones-waya/export', {
        params: periodos.length ? { periodos: periodos.join(',') } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = periodos.length ? `Comisiones_Waya_${periodos.join('_')}.xlsx` : 'Comisiones_Waya.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    }
  };

  const openExclusiones = async () => {
    setShowExcl(true);
    try {
      const r = await api.get('/comisiones-waya/empresas');
      setCatalogo(r.data?.empresas || []);
      setCatalogoInfo({ actualizado: r.data?.actualizado });
      setExclSet(new Set((r.data?.excluidas || []).map(String)));
    } catch (e) { /* noop */ }
  };

  const refrescarCatalogo = async () => {
    try {
      const r = await api.post('/comisiones-waya/empresas/refrescar');
      toast.info('Actualizando lista de empresas desde Waya…');
      startPolling(r.data.job_id, { estado: 'procesando', tipo: 'catalogo' }, async () => {
        const rr = await api.get('/comisiones-waya/empresas');
        setCatalogo(rr.data?.empresas || []);
        setCatalogoInfo({ actualizado: rr.data?.actualizado });
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar la lista');
    }
  };

  const toggleExcluir = (id) => {
    setExclSet((prev) => {
      const n = new Set(prev);
      const k = String(id);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };

  const guardarExclusiones = async () => {
    setSavingExcl(true);
    try {
      await api.put('/comisiones-waya/exclusiones', { excluidas: Array.from(exclSet) });
      toast.success(`Exclusiones guardadas (${exclSet.size})`);
      setShowExcl(false);
    } catch (e) {
      toast.error('No se pudo guardar');
    } finally {
      setSavingExcl(false);
    }
  };

  const running = job && job.estado === 'procesando';
  const shown = meses.filter((m) => selected.has(m.periodo));

  return (
    <div className="space-y-5" data-testid="comisiones-waya-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Coins className="text-teal-600" size={22} />
        <div>
          <h1 className="text-xl font-extrabold text-slate-900">Comisiones (Waya)</h1>
          <p className="text-xs text-slate-500">Totales por mes y desglose por empresa · fuente: Reporte de Liquidaciones de Waya</p>
        </div>
      </div>

      {/* Controles de scraping */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Traer / actualizar mes</label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="month"
                value={scrapeMonth}
                onChange={(e) => setScrapeMonth(e.target.value)}
                className="w-40"
                data-testid="comisiones-month-input"
              />
              <Button onClick={actualizarMes} disabled={running} data-testid="comisiones-actualizar-btn">
                <RefreshCw size={16} className="mr-2" /> Actualizar mes
              </Button>
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200 hidden sm:block" />
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Traer últimos N meses</label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="number" min={1} max={24}
                value={backfillN}
                onChange={(e) => setBackfillN(e.target.value)}
                className="w-20"
                data-testid="comisiones-backfill-input"
              />
              <Button onClick={lanzarBackfill} variant="secondary" disabled={running} data-testid="comisiones-backfill-btn">
                <CalendarClock size={16} className="mr-2" /> Backfill
              </Button>
            </div>
          </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openExclusiones} variant="outline" data-testid="comisiones-excluir-btn">
              <Ban size={16} className="mr-2" /> Excluir empresas
            </Button>
            <Button onClick={exportExcel} variant="outline" disabled={!shown.length} data-testid="comisiones-export-btn">
              <FileSpreadsheet size={16} className="mr-2" /> Exportar Excel
            </Button>
          </div>
        </div>

        {running && (
          <div className="mt-4 rounded-lg border border-teal-200 bg-teal-50 p-3" data-testid="comisiones-progress">
            <div className="flex items-center gap-2 text-sm font-medium text-teal-800">
              <Loader2 size={16} className="animate-spin" />
              {job.tipo === 'catalogo'
                ? 'Actualizando lista de empresas desde Waya…'
                : <>Procesando {job.periodo_actual ? periodoLabel(job.periodo_actual) : ''}{job.mes_total > 1 && ` (mes ${job.mes_idx || 0}/${job.mes_total})`}</>}
            </div>
            {job.empresa_total > 0 && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-teal-100">
                  <div className="h-full bg-teal-500 transition-all" style={{ width: `${Math.round((job.empresa_idx / job.empresa_total) * 100)}%` }} />
                </div>
                <p className="mt-1 text-[11px] text-teal-700">
                  Empresa {job.empresa_idx}/{job.empresa_total}{job.empresa_actual ? ` · ${job.empresa_actual}` : ''}
                </p>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Selector de meses guardados */}
      {meses.length > 0 && (
        <Card className="p-4">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">Meses guardados — elegí cuáles ver</p>
          <div className="flex flex-wrap gap-2" data-testid="comisiones-meses-chips">
            {meses.map((m) => (
              <button
                key={m.periodo}
                onClick={() => toggleSelect(m.periodo)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  selected.has(m.periodo)
                    ? 'border-teal-500 bg-teal-500 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300'
                }`}
                data-testid={`comisiones-chip-${m.periodo}`}
              >
                {periodoLabel(m.periodo)}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Tabla mensual */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : meses.length === 0 ? (
        <Card className="p-10 text-center">
          <Coins className="mx-auto mb-3 text-slate-300" size={40} />
          <p className="text-sm text-slate-500">Todavía no hay meses cargados. Usá <b>“Actualizar mes”</b> o <b>“Backfill”</b> para traer las comisiones desde Waya.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="comisiones-tabla-mensual">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Período</th>
                <th className="px-4 py-3 text-right">Facturado</th>
                <th className="px-4 py-3 text-right">Com. Servicio</th>
                <th className="px-4 py-3 text-right">Com. Medio de Pago</th>
                <th className="px-4 py-3 text-right">Neto</th>
                <th className="px-4 py-3 text-right">Empresas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((m) => {
                const t = m.totales || {};
                const det = expanded[m.periodo];
                return (
                  <React.Fragment key={m.periodo}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <button onClick={() => toggleExpand(m.periodo)} data-testid={`comisiones-expand-${m.periodo}`} className="text-slate-400 hover:text-teal-600">
                          {det ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800">{periodoLabel(m.periodo)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(t.facturado)}</td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-teal-700">{fmtMoney(t.comision_servicio)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(t.comision_medio_pago)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(t.neto)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtInt(m.empresas_con_actividad)}</td>
                    </tr>
                    {det && (
                      <tr>
                        <td colSpan={7} className="bg-slate-50/60 px-4 py-3">
                          {det === 'loading' ? (
                            <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={14} className="animate-spin" /> Cargando detalle…</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-500">
                                <Building2 size={14} /> Desglose por empresa ({(det.empresas || []).length})
                              </div>
                              <table className="w-full text-xs" data-testid={`comisiones-detalle-${m.periodo}`}>
                                <thead className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                                  <tr>
                                    <th className="px-3 py-2">Razón Social</th>
                                    <th className="px-3 py-2">CUIT</th>
                                    <th className="px-3 py-2 text-right">Facturado</th>
                                    <th className="px-3 py-2 text-right">Com. Servicio</th>
                                    <th className="px-3 py-2 text-right">Com. Medio de Pago</th>
                                    <th className="px-3 py-2 text-right">Neto</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {(det.empresas || []).map((e, i) => (
                                    <tr key={i} className="hover:bg-white">
                                      <td className="px-3 py-2 font-medium text-slate-700">{e.razon_social}</td>
                                      <td className="px-3 py-2 tabular-nums text-slate-500">{e.cuit || '—'}</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(e.facturado)}</td>
                                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-teal-700">{fmtMoney(e.comision_servicio)}</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(e.comision_medio_pago)}</td>
                                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(e.neto)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal: excluir empresas */}
      <Dialog open={showExcl} onOpenChange={setShowExcl}>
        <DialogContent className="max-w-2xl" data-testid="comisiones-excluir-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban size={18} className="text-teal-600" /> Excluir empresas de la búsqueda</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Las empresas tildadas <b>no se consultan</b> al traer un mes (útil para las que sabés que no tienen movimientos). Acelera el proceso.
          </p>
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input
                placeholder="Buscar empresa…"
                value={exclSearch}
                onChange={(e) => setExclSearch(e.target.value)}
                className="pl-7"
                data-testid="comisiones-excluir-search"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={refrescarCatalogo} disabled={running} data-testid="comisiones-excluir-refrescar">
              <RefreshCw size={14} className="mr-1" /> Actualizar lista
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {catalogo.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                No hay lista de empresas todavía. Tocá <b>“Actualizar lista”</b> para traerla desde Waya.
              </div>
            ) : (
              catalogo
                .filter((e) => {
                  const q = exclSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (e.razon_social || '').toLowerCase().includes(q) || (e.cuit || '').includes(q);
                })
                .map((e) => (
                  <label key={e.empresa_id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`comisiones-excluir-row-${e.empresa_id}`}>
                    <Checkbox checked={exclSet.has(String(e.empresa_id))} onCheckedChange={() => toggleExcluir(e.empresa_id)} />
                    <span className="flex-1 font-medium text-slate-700">{e.razon_social}</span>
                    <span className="tabular-nums text-xs text-slate-400">{e.cuit || '—'}</span>
                  </label>
                ))
            )}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <span className="text-xs text-slate-500">{exclSet.size} excluida(s) · {catalogo.length} empresas</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowExcl(false)}>Cancelar</Button>
              <Button onClick={guardarExclusiones} disabled={savingExcl} data-testid="comisiones-excluir-guardar">
                {savingExcl ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Guardar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
